import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { usePtaAccess } from "@/hooks/usePtaAccess";

interface ProtectedRouteProps {
  children: ReactNode;
  requireRole?: string;
  requireAddon?: string;
}

export const ProtectedRoute = ({ children, requireRole, requireAddon }: ProtectedRouteProps) => {
  const { user, loading, hasRole, hasPlan, isOrphaned, isPtaAdmin, hasPta, subscription } = useAuth();
  const { isTrialExpired, canAccessDashboard, loading: trialLoading } = useTrialStatus();
  const { hasAddon, loading: addonsLoading } = usePtaAccess();
  const location = useLocation();
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [checkingVerification, setCheckingVerification] = useState(true);

  useEffect(() => {
    if (!user) {
      setCheckingVerification(false);
      return;
    }

    // Use email_confirmed_at from auth.users as single source of truth
    const isVerified = !!user.email_confirmed_at;
    setEmailVerified(isVerified);
    setCheckingVerification(false);
  }, [user]);

  if (loading || trialLoading || checkingVerification || addonsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Preserve post-payment return so user lands back on the right tab after re-auth
    const search = location.search || "";
    if (search.includes("payment=success")) {
      const redirectTarget = encodeURIComponent(`${location.pathname}${search}`);
      return <Navigate to={`/auth?redirect=${redirectTarget}`} replace />;
    }
    return <Navigate to="/auth" replace />;
  }

  // Check if email is verified (site admins bypass this)
  const isSiteAdmin = hasRole('site_admin');
  if (!isSiteAdmin && emailVerified === false) {
    return <Navigate to={`/check-email?email=${encodeURIComponent(user.email || '')}`} replace />;
  }

  // Check trial status and restrict access if trial expired
  const isSiteAdminCheck = hasRole('site_admin');

  // Orphaned member (PTA was deleted) — block access except for the notice page itself
  if (isOrphaned && !isSiteAdminCheck && location.pathname !== '/account-removed') {
    return <Navigate to="/account-removed" replace />;
  }

  // Incomplete onboarding: signed up + verified but never created a PTA or picked a plan.
  // Bounce back to /onboarding so they can finish.
  const ALLOWED_INCOMPLETE_PATHS = ['/onboarding', '/auth', '/check-email', '/verify-email', '/settings', '/'];
  if (
    !isSiteAdminCheck &&
    !isPtaAdmin &&
    !hasPta &&
    !subscription &&
    !isOrphaned &&
    !ALLOWED_INCOMPLETE_PATHS.includes(location.pathname)
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  if (isTrialExpired && !canAccessDashboard(location.pathname)) {
    return <Navigate to="/trial-expired" replace />;
  }

  // Site admins have access to everything
  if (requireRole && !isSiteAdmin && !hasRole(requireRole)) {
    return <Navigate to="/" replace />;
  }

  // Check add-on access: Basic plan users need the specific add-on purchased
  if (requireAddon && !isSiteAdmin && !hasPlan("standard") && !hasAddon(requireAddon)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
